import api from '../api'

export const fetchJobList = async ({ 
  search, 
  page, 
  pageSize, 
  filters = {},
  fields,
}: { 
  search: string; 
  page: number; 
  pageSize: number;
  filters?: { [key: string]: string[] },
  fields?: string,
}) => {
  const params: Record<string, any> = {
    search,
    page,
    page_size: pageSize,
    expand: 'skills.skill',
    ...(fields ? { fields } : {}),
  }
  Object.entries(filters).forEach(([key, values]) => params[`${key}__in`] = values.join(','))
  const response = await api.get('/classifications/job-profiles/', { params })
  return response.data
}

export const fetchJob = async (jobId: string) => {
  const response = await api.get(`/classifications/job-profiles/${jobId}/`, {
    params: {
      expand: 'skills.skill',
    },
  })
  return response.data
}

export const addJob = async (job: any) => {
  const response = await api.post('/classifications/job-profiles/', job)
  return response.data
}

export const updateJob = async (job: any) => {
  const response = await api.patch(`/classifications/job-profiles/${job.id}/`, job)
  return response.data
}

export const deleteJob = async (jobId: number) => {
  const response = await api.delete(`/classifications/job-profiles/${jobId}/`)
  return response.data
}

export const fetchSkillList = async ({
  search,
  page,
  pageSize,
  type,
  filters,
}: {
  search: string
  page: number
  pageSize: number
  type?: 'skill' | 'competence' | 'task'
  filters: { [key: string]: string[] }
}) => {
  const params: Record<string, any> = {
    search,
    page,
    page_size: pageSize,
  }

  // For each filter, if it has multiple values, use __in, otherwise use the single value.
  Object.entries(filters).forEach(([key, values]) => {
    if (values.length > 1) {
      params[`${key}__in`] = values.join(',')
    } else if (values.length === 1) {
      params[key] = values[0]
    }
  })

  // If there is no 'type' filter provided, use the default type.
  if (!filters.type) {
    params['type'] = type
  }

  const response = await api.get('/classifications/skills/', { params })
  return response.data
}

export const fetchSkill = async (skillId: string) => {
  const response = await api.get(`/classifications/skills/${skillId}/`)
  return response.data
}

export const addSkill = async (skill: any) => {
  const response = await api.post('/classifications/skills/', skill)
  return response.data
}

export const updateSkill = async (skill: any) => {
  const response = await api.patch(`/classifications/skills/${skill.id}/`, skill)
  return response.data
}

export const deleteSkill = async (skillId: number) => {
  const response = await api.delete(`/classifications/skills/${skillId}/`)
  return response.data
}

// Roles
export const fetchRoleList = async ({
  search,
  page,
  pageSize,
}: {
  search: string
  page: number
  pageSize: number
}) => {
  const response = await api.get('/classifications/roles/', {
    params: { search, page, page_size: pageSize },
  })
  return response.data
}

export const addRole = async (role: { title: string; description: string }) => {
  const response = await api.post('/classifications/roles/', role)
  return response.data
}

export const updateRole = async (role: { id: string; title: string; description: string }) => {
  const response = await api.patch(`/classifications/roles/${role.id}/`, role)
  return response.data
}

export const deleteRole = async (roleId: number | string) => {
  const response = await api.delete(`/classifications/roles/${roleId}/`)
  return response.data
}

// Accountability Areas
export const fetchAccountabilityAreaList = async ({
  search,
  page,
  pageSize,
}: {
  search: string
  page: number
  pageSize: number
}) => {
  const response = await api.get('/classifications/accountability-areas/', {
    params: { search, page, page_size: pageSize },
  })
  return response.data
}

export const addAccountabilityArea = async (area: { title: string; description: string }) => {
  const response = await api.post('/classifications/accountability-areas/', area)
  return response.data
}

export const updateAccountabilityArea = async (area: { id: string; title: string; description: string }) => {
  const response = await api.patch(`/classifications/accountability-areas/${area.id}/`, area)
  return response.data
}

export const deleteAccountabilityArea = async (areaId: number | string) => {
  const response = await api.delete(`/classifications/accountability-areas/${areaId}/`)
  return response.data
}

export const exportJobsToXls = async ({
  search,
  filters,
  ids,
}: {
  search?: string
  filters?: { [key: string]: string[] }
  ids?: number[]
}) => {
  const params: any = {}

  if (search) params.search = search
  if (ids?.length) params.ids = ids.join(',')

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value?.length) params[key] = value.join(',')
    })
  }

  const response = await api.get('/classifications/job-profiles/export-xls/', {
    params,
    responseType: 'blob',
  })

  const blob = new Blob([response.data])
  const url = window.URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url

  const disposition = response.headers['content-disposition']
  let filename = 'job_profiles.xlsx'

  if (disposition) {
    const match = disposition.match(/filename="(.+)"/)
    if (match?.[1]) filename = match[1]
  }

  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()

  window.URL.revokeObjectURL(url)
}

export const exportJobsToCsv = async ({
  search,
  filters,
  ordering,
  ids,
}: {
  search?: string
  filters?: { [key: string]: string[] }
  ordering?: string
  ids?: number[]
}) => {
  const params: Record<string, any> = {}

  if (search) params.search = search
  if (ordering) params.ordering = ordering

  if (filters) {
    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 1) {
        params[`${key}__in`] = values.join(',')
      } else if (values.length === 1) {
        params[key] = values[0]
      }
    })
  }

  if (ids && ids.length > 0) {
    params.ids = ids.join(',')
  }

  const response = await api.get(
    '/classifications/job-profiles/export-csv/',
    {
      params,
      responseType: 'blob',
    },
  )

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')

  const contentDisposition = response.headers['content-disposition']
  let filename = 'job_profiles_export.csv'

  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/)
    if (match?.[1]) filename = match[1]
  }

  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
